import { useState } from "react";
import type { Order, ParkedOrder } from "../../api/types";
import { useTill } from "../../app/till-context";
import { t } from "../../i18n";
import { formatEuro } from "../../lib/money";
import { formatTime } from "../../lib/time";
import { contentOf } from "../orders/order-view";
import { printToasts } from "../printing/print-notices";
import { CheckoutDialog } from "../sale/CheckoutDialog";

interface Props {
  parked: readonly ParkedOrder[];
  /**
   * "Betalen bij afhalen" from the Tafo app (blok J): takeaways already in the kitchen, paid here when
   * the customer comes for them.
   */
  awaiting: readonly Order[];
  busy: boolean;
  onResume: (entry: ParkedOrder) => void;
  onDrop: (entry: ParkedOrder) => void;
}

/**
 * De Wachtlijst (prototype renderParked, Ctrl+4): the tickets put aside with Parkeren, the same on
 * every device. "Verder werken" brings one back on the ticket of this till; "Weggooien" removes it.
 * The takeaways a waiter sent to the kitchen with "Betalen bij afhalen" wait here to be paid.
 */
export function ParkedScreen({ parked, awaiting, busy, onResume, onDrop }: Props) {
  const text = t.parked;
  const { toast } = useTill();
  const [paying, setPaying] = useState<Order | null>(null);
  return (
    <div className="panel admin-section parked">
      <h2>{text.title}</h2>
      {awaiting.length > 0 && (
        <>
          <h3>{text.awaiting}</h3>
          <div className="cols">
            {awaiting.map((order) => (
              <div className="card" key={order.id} aria-label={order.code}>
                <b>
                  {formatTime(order.createdAt)} · {text.channel.takeaway} ·{" "}
                  <span className="num">{formatEuro(order.totalCents - order.paidCents)}</span>
                </b>
                <span>{contentOf(order)}</span>
                <span className="proto">
                  {[order.customer.name, order.code].filter(Boolean).join(" · ")} · {text.by(order.createdBy ?? "—")} ·{" "}
                  {text.inKitchen}
                </span>
                <div className="acts">
                  <button
                    type="button"
                    className="btn primary"
                    onClick={() => {
                      setPaying(order);
                    }}
                  >
                    {text.pay}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
      {parked.length === 0 && awaiting.length === 0 ? (
        <p className="note">{text.empty}</p>
      ) : (
        parked.length > 0 && (
          <div className="cols">
            {parked.map((entry) => (
              <div className="card" key={entry.id}>
                <b>
                  {formatTime(entry.createdAt)} · {text.channel[entry.channel]} ·{" "}
                  <span className="num">{formatEuro(entry.totalCents)}</span>
                </b>
                <span>{entry.summary}</span>
                <span className="proto">
                  {[entry.customer.name, entry.customer.phone].filter(Boolean).join(" · ") || text.noCustomer} ·{" "}
                  {text.by(entry.createdBy)}
                </span>
                <div className="acts">
                  <button
                    type="button"
                    className="btn"
                    disabled={busy}
                    onClick={() => {
                      onResume(entry);
                    }}
                  >
                    {text.resume}
                  </button>
                  <button
                    type="button"
                    className="btn"
                    disabled={busy}
                    onClick={() => {
                      onDrop(entry);
                    }}
                  >
                    {text.drop}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )
      )}
      {paying && (
        <CheckoutDialog
          target={{ kind: "existing", order: paying }}
          onPaid={(order) => {
            toast(`${t.sale.saved(order.code)} · ${formatEuro(order.totalCents)}`);
            for (const message of printToasts(order.prints)) toast(message);
            setPaying(null);
          }}
          onDeferred={() => {
            setPaying(null);
          }}
          onClose={() => {
            setPaying(null);
          }}
        />
      )}
    </div>
  );
}
